import { types, flow, getEnv, getParent } from 'mobx-state-tree';
import { toJS } from 'mobx';
import orderBy from 'lodash-es/orderBy';
import { differenceInDays, isPast, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { clean, policyDetailsFromSegments } from '@ourbranch/policy-utils';
import { onboardingTasks, reopeningStates } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

import { types as GQLTypes } from 'core/helpers/sanitize';
import Notification, { isPolicyCrossSellEligible } from 'core/helpers/notifications';
import { checkIfUnauthorizedAndPushToSearchPage, formattedError } from 'core/helpers/quoter.service';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { RESET_PWD, UPDATE_ACCOUNT } from 'customer/components/account-details/account-details.queries';
import { CHANGE_SEGMENT, CONFIRM } from 'customer/components/policy/policy.queries';
import { getMerged } from 'customer/components/policy/merge-segments';
import {
  GET_ACCOUNT,
  GET_FULL_ACCOUNT,
  GET_ACCOUNT_POLICY,
  GET_TICKETS,
  GET_FRONT_CONTACT,
  GET_ACCOUNT_CLAIMS,
  SEND_EVENT_AS_MEMBER,
  COMPLETE_TASK,
  RESET_TASK,
  APPROVE_TASK,
  GET_DOCS_AND_TASKS,
  GET_ACCOUNT_BRANCH_PLUS_POLICY,
  GET_SNAPSHEET_EXPOSURE_DETAIL,
  GET_SNAPSHEET_NOTES_DETAIL,
  GET_MEMBER_INVENTORY_IMAGES
} from './account.queries';
import { PoliciesStore } from './policies-store';
import { calculateMarketTier } from 'core/helpers/misc';

const AdditionalPhoneNumber = types.model({
  phoneNumber: types.maybeNull(types.string),
  note: types.maybeNull(types.string),
  canText: types.maybeNull(types.boolean)
});
const Error = types.model({
  code: types.union(types.string, types.number),
  message: types.maybeNull(types.string),
  data: types.maybeNull(types.frozen())
});

export const AccountStore = types
  .model({
    id: types.string,
    fname: types.string,
    lname: types.string,
    phoneNumber: types.string,
    additionalPhoneNumbers: types.maybeNull(types.optional(types.array(AdditionalPhoneNumber), [])),
    email: types.string,
    created: types.string,
    mailingAddress: types.maybeNull(types.frozen()),
    accountLoading: types.optional(types.boolean, false),
    ticketsLoading: types.optional(types.boolean, false),
    frontTicketsLoading: types.optional(types.boolean, false),
    tickets: types.maybeNull(
      types.model({
        frontTickets: types.frozen(),
        pointers: types.maybeNull(
          types.model({
            current: types.optional(types.string, ''),
            next: types.optional(types.string, ''),
            prev: types.optional(types.array(types.string), [])
          })
        ),
        sendGridEmails: types.array(types.frozen())
      }),
      null
    ),
    policies: types.maybe(PoliciesStore),
    branchPlusPolicies: types.model({
      list: types.array(types.frozen(), []),
      branchPlusPolicy: types.model({
        loading: types.optional(types.boolean, false),
        policy: types.maybeNull(types.frozen())
      }),
      loading: types.optional(types.boolean, false)
    }),
    pledges: types.frozen(),
    community: types.frozen(),
    inviter: types.maybeNull(types.frozen()),
    claims: types.optional(types.array(types.frozen()), [], [null, undefined]),
    claimsLoading: types.optional(types.boolean, false),
    exposureDetails: types.optional(types.array(types.frozen()), [], [null, undefined]),
    exposureDetailsLoading: types.optional(types.boolean, false),
    notesDetail: types.optional(types.array(types.frozen()), [], [null, undefined]),
    notesDetailLoading: types.optional(types.boolean, false),
    notifications: types.maybeNull(types.array(types.string)),
    tasks: types.optional(types.array(types.frozen()), []),
    customerMetadata: types.maybeNull(types.frozen()),
    tasksError: types.maybeNull(types.string),
    frontContactURL: types.maybeNull(types.string),
    errors: types.array(Error),
    marketTierRating: types.maybeNull(types.number),
    inventoryItems: types.optional(
      types.array(
        types.model({
          key: types.string,
          url: types.string
        })
      ),
      []
    ),
    inventoryItemsLoading: types.optional(types.boolean, false),
    startedUploadingInventoryImages: types.optional(types.boolean, false)
  })
  .actions((self) => ({
    handleError(error) {
      const parsed = formattedError(error);
      if (parsed) {
        self.accountLoading = false;
        const errors = [...self.errors, parsed];
        self.errors = errors;
      }
      return parsed;
    },
    fetchInventoryItems: flow(function* fetchInventoryItems() {
      const { client } = getEnv(self);
      self.inventoryItemsLoading = true;

      try {
        const { data } = yield client.query({
          query: GET_MEMBER_INVENTORY_IMAGES,
          variables: { accountId: self.id }
        });

        if (data?.getMemberInventoryImages?.images) {
          self.inventoryItems = data.getMemberInventoryImages.images;
        } else {
          self.inventoryItems = [];
        }
      } catch (error) {
        self.handleError(error);
        self.inventoryItems = [];
      } finally {
        self.inventoryItemsLoading = false;
      }
    }),
    fetchFullAccount: flow(function* fetchFullAcount(id, history, isAgency) {
      const { client } = getEnv(self);
      self.tasksError = null;
      self.accountLoading = true;
      self.policies.loading = true;
      self.branchPlusPolicies.loading = true;
      self.errors = [];
      try {
        const { data } = yield client.query({
          query: GET_FULL_ACCOUNT,
          variables: {
            id
          }
        });
        const {
          account,
          documents,
          community,
          pledges,
          unsignedApplications,
          unsignedBixConversions,
          customerMetadata,
          tasks: { success, tasks, error },
          branchPlusPolicies
        } = data;
        self.setAccount(account);
        self.policies.list = account.policies;
        self.startedUploadingInventoryImages = account.startedUploadingInventoryImages;
        self.branchPlusPolicies.list = branchPlusPolicies;
        self.policies.documents = documents;
        self.pledges = pledges;
        self.community = community;
        self.policies.unsignedApplications = unsignedApplications;
        self.policies.unsignedBixConversions = unsignedBixConversions || [];
        self.customerMetadata = customerMetadata;
        self.fetchAffinityData();
        self.marketTierRating = self.getMemberMarketTier(account.policies);
        const policyIdsNeedingAchVerificaton = self.policies.getPoliciesNeedingStripeBankVerification();
        if (policyIdsNeedingAchVerificaton.length > 0) {
          yield self.policies.generateAndSaveStripeBankInfoToPolicies(id, policyIdsNeedingAchVerificaton);
        }
        if (success) {
          self.setOngoingTasks(tasks);
        } else {
          self.tasksError = error;
        }
      } catch (error) {
        self.handleError(error);
        checkIfUnauthorizedAndPushToSearchPage(error, history);
      } finally {
        self.accountLoading = false;
        self.policies.loading = false;
        self.branchPlusPolicies.loading = false;
        self.setNotifications(isAgency);
      }
    }),
    fetchFullAccountAndPolicy: flow(function* fetchFullAccountAndPolicy(id, accountId, history, isAgencyUser) {
      const { client } = getEnv(self);
      self.errors = [];
      self.tasksError = null;
      self.accountLoading = true;
      self.policies.policy.loading = true;
      self.policies.policy.holdCardLoading = true;
      self.policies.loadingDocuments = true;
      try {
        const { data } = yield client.query({
          query: GET_ACCOUNT_POLICY,
          fetchPolicy: 'no-cache',
          variables: {
            id,
            accountId
          }
        });
        const {
          account,
          billingDetails,
          writeOffTransactions,
          documents,
          holdcards,
          inspection,
          unsignedBixConversions,
          unsignedApplications,
          customerMetadata,
          violationsForRenewal,
          tasks: { success, tasks, error },
          policyPaidAndPendingCash: { currentTotalPaid, pendingAmount }
        } = data;
        self.policies.list = account.policies;
        self.fetchAffinityData();
        self.setAccount(account);
        // fill out child PolicyStore info
        self.policies.policy.billingDetails = billingDetails;
        self.policies.policy.writeOffTransactions = writeOffTransactions;
        self.policies.policy.holdCards = holdcards;
        self.policies.policy.inspection = inspection;
        self.policies.unsignedApplications = unsignedApplications;
        self.policies.documents = documents;
        self.customerMetadata = customerMetadata;
        self.policies.policy.currentTotalPaid = currentTotalPaid;
        self.policies.policy.pendingAmount = pendingAmount;
        if (success) {
          self.setOngoingTasks(tasks);
        } else {
          self.tasksError = error;
        }
        const selectedPolicy = account.policies.find((p) => p.id === id);
        if (selectedPolicy?.billingHoldUntil && isPast(parseISO(selectedPolicy.billingHoldUntil))) {
          self.policies.policy.policy = { ...selectedPolicy, billingHoldUntil: null };
        } else {
          self.policies.policy.policy = selectedPolicy;
        }
        const { fees, segments } = selectedPolicy;
        self.policies.policy.fees = fees;
        const mappedSegments = segments.slice().map((s) => clean(cloneDeep(toJS(s))));
        self.policies.policy.segments = cloneDeep(mappedSegments);
        const currentlySelectedSegmentId =
          self.policies.policy?.segment?.segmentId || mappedSegments[mappedSegments.length - 1].segmentId;
        self.policies.policy.segment = getMerged(cloneDeep(mappedSegments), currentlySelectedSegmentId);
        self.policies.unsignedBixConversions = unsignedBixConversions || {};
        const bixConversionDocsInfo = self.policies.getUnsignedBixConversion(id);
        if (!bixConversionDocsInfo.signedDocUploadedInS3) {
          // If entered, there are no signed documents in S3, but there could be a delay up to 3 hours from
          // when the person signed to when the signed pdfs are uploaded. so, check if that's the case
          // if so, the below function updates the policy store with the signed timestamp
          yield self.policies.policy.getBixConversationSignatureFromTable(id);
        }
        self.policies.policy.setViolationsForRenewal(violationsForRenewal);
        const policyIdsNeedingAchVerificaton = self.policies.getPoliciesNeedingStripeBankVerification();
        if (policyIdsNeedingAchVerificaton.length > 0) {
          yield self.policies.generateAndSaveStripeBankInfoToPolicies(accountId, policyIdsNeedingAchVerificaton);
        }
      } catch (error) {
        self.handleError(error);
        if (history) {
          checkIfUnauthorizedAndPushToSearchPage(error, history);
        }
      } finally {
        self.accountLoading = false;
        self.policies.policy.loading = false;
        self.policies.policy.holdCardLoading = true;
        self.policies.loadingDocuments = false;
        self.policies.policy.policyEquityStatus.status = 'done';
        self.policies.setNotifications(isAgencyUser);
        self.setNotifications(isAgencyUser);
      }
    }),
    fetchFullAccountAndBranchPlusPolicy: flow(
      function* fetchFullAccountAndBranchPlusPolicy(branchPlusPolicyId, accountId, history, isAgencyUser) {
        const { client } = getEnv(self);
        self.accountLoading = true;
        self.branchPlusPolicies.branchPlusPolicy.loading = true;

        self.errors = [];
        try {
          const { data } = yield client.query({
            query: GET_ACCOUNT_BRANCH_PLUS_POLICY,
            variables: {
              accountId,
              policyId: branchPlusPolicyId
            }
          });
          const { account, branchPlusPolicies } = data;
          self.setAccount(account);
          self.branchPlusPolicies.list = branchPlusPolicies;
          const branchPlusPolicy = cloneDeep(
            branchPlusPolicies.find(({ policyId }) => policyId === branchPlusPolicyId)
          );
          const { carriers } = getParent(self);
          const carrierInfo = yield carriers.getCarrierInformation(
            branchPlusPolicy.carrier.name,
            branchPlusPolicy.policyType
          );

          if (carrierInfo) {
            branchPlusPolicy.carrier = {
              ...branchPlusPolicy.carrier,
              ...carrierInfo
            };
          }
          self.branchPlusPolicies.branchPlusPolicy.policy = branchPlusPolicy;
        } catch (error) {
          self.handleError(error);
          if (history) {
            checkIfUnauthorizedAndPushToSearchPage(error, history);
          }
        } finally {
          self.accountLoading = false;
          self.branchPlusPolicies.branchPlusPolicy.loading = false;
          self.setNotifications(isAgencyUser);
        }
      }
    ),
    fetchAccount: flow(function* getAccount(id) {
      const { client } = getEnv(self);
      self.errors = [];
      self.tasksError = null;
      self.accountLoading = true;
      try {
        const { data } = yield client.query({
          query: GET_ACCOUNT,
          variables: {
            id
          }
        });
        const { account } = data;
        self.setAccount(account);
      } catch (error) {
        self.handleError(error);
      } finally {
        self.accountLoading = false;
      }
    }),
    fetchAffinityData: flow(function* fetchAffinityData() {
      const affinities = [];
      if (self.policies?.list?.length) {
        for (const policy of self.policies.list) {
          const policyAffinities = Array.from(
            new Set(policy.segments.map((segment) => segment.global?.affinity).filter((affinity) => affinity))
          );
          affinities.push(...policyAffinities);
        }
      }
      if (affinities.length > 0) {
        const { affinityLookups } = getParent(self);
        yield affinityLookups.getByAffinityList(affinities, 'ALL');
      }
    }),
    fetchTickets: flow(function* getTickets({ frontTicketsSearchConfig = {} } = {}) {
      const { client } = getEnv(self);
      self.ticketsLoading = !frontTicketsSearchConfig;
      self.frontTicketsLoading = !!frontTicketsSearchConfig;
      self.errors = [];
      const { id: accountId, fname, lname, email, phoneNumber, frontTagId } = self;
      const { page: nextPage, prevPage, itemsPerPage } = frontTicketsSearchConfig;

      const nextPageToken = self.getFrontPageToken({ prevPage, newPage: nextPage });
      try {
        const { data } = yield client.query({
          query: GET_TICKETS,
          variables: {
            params: {
              accountId,
              fname,
              lname,
              email,
              phoneNumber,
              frontTicketsSearchConfig: {
                itemsPerPage,
                page: nextPage,
                tagId: frontTagId,
                pageToken: nextPageToken
              }
            }
          }
        });
        if (self.frontTicketsLoading) {
          self.tickets = {
            ...self.tickets,
            frontTickets: data.tickets.frontTickets
          };
        } else {
          self.tickets = data.tickets;
        }

        const prevPointers = self.tickets.pointers?.prev || [];
        self.tickets.pointers = {
          next: data.tickets.frontTickets.nextFrontPageToken || undefined,
          // If going forward, store prev pointers in a stack to enable backwards pagination
          prev: nextPage > prevPage ? [...prevPointers, self.tickets.pointers.current] : prevPointers,
          current: nextPageToken
        };
      } catch (error) {
        self.handleError(error);
      } finally {
        self.ticketsLoading = false;
        self.frontTicketsLoading = false;
      }
    }),
    fetchFrontContactURL: flow(function* getFrontContactURL(email) {
      if (!email) {
        return;
      }
      const { client } = getEnv(self);
      self.tasksError = null;
      self.errors = [];
      try {
        const { data } = yield client.query({
          query: GET_FRONT_CONTACT,
          variables: {
            email
          }
        });
        const { frontContactURL } = data;
        self.frontContactURL = frontContactURL;
      } catch (error) {
        self.handleError(error);
      }
    }),
    resetPwd: flow(function* resetPwd(accountId) {
      const { client } = getEnv(self);
      return yield client.mutate({
        mutation: RESET_PWD,
        variables: {
          username: accountId
        }
      });
    }),
    sendEventAsMember: flow(function* sendEventAsMember(eventDetails) {
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: SEND_EVENT_AS_MEMBER,
          variables: {
            eventDetails
          }
        });
        return { data };
      } catch (error) {
        return { error: error.message };
      } finally {
        self.accountLoading = false;
      }
    }),
    updateAccount: flow(function* updateAccount(newAccountDetails) {
      self.accountLoading = true;
      const { client } = getEnv(self);
      try {
        const { data } = yield client.mutate({
          mutation: UPDATE_ACCOUNT,
          variables: {
            account: newAccountDetails
          }
        });
        self.setAccount(data.updateAccount);
        return { data };
      } catch (error) {
        return { error: error.message };
      } finally {
        self.accountLoading = false;
      }
    }),
    completeTask: flow(function* completeTask({
      taskId,
      accountId,
      documentUrl,
      files,
      policyId,
      taskName,
      submittedByIA
    }) {
      self.tasksError = null;
      self.accountLoading = true;
      const { client } = getEnv(self);
      const completeTaskVariables = {
        taskDetails: { taskId, accountId, documentUrl, files, policyId, taskName }
      };
      const policy = self.policies?.list?.find(({ id }) => id === policyId);

      if (
        taskName === onboardingTasks.CompleteInventory &&
        policy &&
        policy.policyType === policyTypes.Home &&
        reopeningStates.isStateReopenedForPolicyType(policy.offer.quote.correctedAddress.state, policyTypes.Home) &&
        policy.offer.quote.home.yearBuilt &&
        policy.offer.quote.home.yearBuilt <= 1940
      ) {
        completeTaskVariables.ticket = {
          subject: 'Home inventory completed - 1940 or older',
          body: `Policy ${policyId} was bound with an effective date of ${policy.effectiveDate}. Please review the completed inventory and take action if needed.`
        };
      }

      const iaSubmittedTasksThatNeedFrontTicket = [
        onboardingTasks.NeedProofOfPriorCoverage,
        onboardingTasks.ProvideProofOfConnectedHome,
        onboardingTasks.ConnectedHomeRenewal,
        onboardingTasks.NeedProofThatAccidentPreventionCourseTaken,
        onboardingTasks.ConfirmWindstormMitigationStatus
      ];

      if (submittedByIA && iaSubmittedTasksThatNeedFrontTicket.includes(taskName)) {
        completeTaskVariables.ticket = {
          subject: `"${taskName}" task submitted by external agent`,
          body: `"${taskName}" task was submitted by an external agent for policy ${policyId}. Please review the task and take action if needed. \n Link to the policy: https://staff.ourbranch.com/customer/${accountId}/policy/${policyId}`
        };
      }
      const { data } = yield client.mutate({
        mutation: COMPLETE_TASK,
        variables: completeTaskVariables
      });
      const {
        completeTask: { success }
      } = data;
      if (!success) {
        const { error } = data;
        return { error };
      }
      if (success) {
        if (taskName === onboardingTasks.CompleteInventory) {
          const { segments } = self.policies.list.find(({ id }) => id === policyId);
          const { startDate, segmentId } = segments[segments.length - 1];
          const {
            data: { previewPolicyChangeToSegment }
          } = yield client.mutate({
            mutation: CHANGE_SEGMENT,
            variables: {
              policyId,
              segmentId,
              segment: GQLTypes.PolicyDetailsInput({
                startDate,
                global: { manualInventorySubmission: true }
              })
            }
          });

          yield client.mutate({
            mutation: CONFIRM,
            variables: {
              policyId,
              previewId: previewPolicyChangeToSegment.id,
              internalNotes: 'Completed inventory task -> Manually submitted inventory'
            }
          });
        }
        yield self.loadDocsAndTasks(accountId);
        return { ok: success };
      }
    }),
    approveTask: flow(function* approveTask({ taskId, accountId, policyId }) {
      self.tasksError = null;
      self.accountLoading = true;
      const { client } = getEnv(self);
      const { data } = yield client.mutate({
        mutation: APPROVE_TASK,
        variables: { taskDetails: { taskId, accountId, policyId } }
      });
      const {
        approveTask: { success }
      } = data;
      if (success) {
        self.accountLoading = false;
        yield self.loadDocsAndTasks(accountId);
        return { ok: true };
      }
      self.accountLoading = false;
      return { ok: false, error: 'Failed to approve task' };
    }),
    resetTask: flow(function* resetTask({ taskId, accountId, policyId, taskName, completedBy }) {
      self.tasksError = null;
      self.accountLoading = true;
      const { client } = getEnv(self);
      const { data } = yield client.mutate({
        mutation: RESET_TASK,
        variables: { taskDetails: { taskId, accountId, policyId, taskName, completedBy } }
      });
      const {
        resetTask: { success }
      } = data;
      if (!success) {
        const { error } = data;
        return { error };
      }
      if (success) {
        yield self.loadDocsAndTasks(accountId);
        return { ok: success };
      }
    }),
    loadDocsAndTasks: flow(function* loadDocsAndTasks(accountId) {
      const { client } = getEnv(self);
      const { data } = yield client.query({
        query: GET_DOCS_AND_TASKS,
        variables: { accountId }
      });
      const {
        documents,
        tasks: { success, tasks, error },
        unsignedApplications
      } = data;
      self.policies.unsignedApplications = unsignedApplications;
      self.policies.documents = documents;
      if (success) {
        self.setOngoingTasks(tasks);
      } else {
        self.tasksError = error;
      }

      self.accountLoading = false;
      self.setNotifications();
    })
  }))
  .actions((self) => ({
    setAccount(account) {
      self.id = account.id || self.id;
      self.fname = account.fname || self.fname;
      self.lname = account.lname || self.lname;
      self.phoneNumber = account.phoneNumber || self.phoneNumber;
      self.email = account.email || self.email;
      self.created = account.created || self.created;
      self.mailingAddress = account.mailingAddress || self.mailingAddress;
      self.inviter = account.inviter || self.inviter;
      self.additionalPhoneNumbers = account.additionalPhoneNumbers || self.additionalPhoneNumbers;
      self.frontTagId = account.frontTagId;
    },
    setNotifications: function setNotifications(isAgencyUser) {
      const notifications = [];

      const activePolicies = self.policies?.list?.filter((p) => getPolicyStatus(p) === PolicyStatus.Active) || [];

      const isPolicyAgencySold = self.policies.list[0]?.isAgentSold || self.policies.list[0]?.offer?.quote?.isAgentSold;

      const parentStore = getParent(self);
      const affinityOnPolicy = self.policies.list[0]?.segments?.[0]?.global?.affinity;
      const affinityData =
        affinityOnPolicy && parentStore.affinityLookups.data.find(({ affinity }) => affinity === affinityOnPolicy);
      const serviceAgreement = affinityData?.data?.serviceAgreement;

      const policies = self.policies?.list || [];
      const accountHasCrossSellEligiblePolicy = (() => {
        const monolineActivePolicy = activePolicies.length === 1 ? activePolicies[0] : null;
        if (!monolineActivePolicy) {
          return false;
        }

        const eligibleCrossSellTypes = [policyTypes.Home, policyTypes.Auto];
        if (!eligibleCrossSellTypes.includes(monolineActivePolicy.policyType)) {
          return false;
        }

        const activeOrFutureStatuses = [PolicyStatus.Active, PolicyStatus.Future];
        const bundledTypes =
          monolineActivePolicy.policyType === policyTypes.Home
            ? [policyTypes.Auto, policyTypes.HABundle, policyTypes.ARBundle, policyTypes.CABundle]
            : [policyTypes.Home, policyTypes.HABundle];

        const bundledPolicyExists = policies.some((policy) => {
          if (!activeOrFutureStatuses.includes(getPolicyStatus(policy))) {
            return false;
          }
          return bundledTypes.includes(policy.policyType);
        });

        if (bundledPolicyExists) {
          return false;
        }

        const isActivePolicyAgencySold =
          monolineActivePolicy.isAgentSold || monolineActivePolicy.offer?.quote?.isAgentSold;

        return isPolicyCrossSellEligible(monolineActivePolicy, isActivePolicyAgencySold, isAgencyUser);
      })();

      if (accountHasCrossSellEligiblePolicy) {
        notifications.push(Notification.Customer.CrossSellEligibility);
      }

      if (self?.incompleteTasks.length) {
        notifications.push(Notification.Customer.IncompleteTasks);
      }

      if (!isAgencyUser && isPolicyAgencySold && !serviceAgreement && affinityData) {
        notifications.push(Notification.Customer.TransferToAgency);
      }

      if (!isAgencyUser && isPolicyAgencySold && serviceAgreement && affinityData) {
        notifications.push(Notification.Customer.AgencyServicedByBranch);
      }

      if (notifications.length) {
        self.notifications = notifications;
      } else {
        self.notifications = null;
      }
    },
    setOngoingTasks(tasks) {
      self.tasks = tasks
        .filter(({ taskName, policyId }) => {
          const tasksToExclude = [
            onboardingTasks.StressFreeSwitchAuto,
            onboardingTasks.StressFreeSwitchHome,
            onboardingTasks.StressFreeSwitchRenters,
            onboardingTasks.StressFreeSwitchUmbrella
          ];
          // First check: must have policyId and exclude stress-free switch tasks
          if (!policyId || tasksToExclude.includes(taskName)) {
            return false;
          }

          const nonExpiringTasks = [onboardingTasks.ReviewPotentialUndisclosedDriver];
          if (nonExpiringTasks.includes(taskName)) {
            return true;
          }
          // All other tasks are subject to the 100-day policy age rule
          const policy = self.policies.list.find(({ id }) => id === policyId);
          if (policy) {
            const today = new Date();
            const effectiveDate = parseISO(policy.effectiveDate);
            return differenceInDays(new Date(effectiveDate), today) < 100;
          }
          return false;
        })
        .map(({ data, ...task }) => {
          const parsedData = data ? JSON.parse(data) : null;

          // Transform single documentURL to files format for backward compatibility
          // previously uploaded task documents were only stored the documentURL
          if (parsedData) {
            if (parsedData.documentUrl) {
              parsedData.files = [
                {
                  documentUrl: parsedData.documentUrl,
                  fileName: parsedData.documentUrl.split('/').pop(),
                  uploadedAt: parsedData.uploadedAt || null
                }
              ];
            }
          }

          return {
            ...task,
            data: parsedData,
            completed: !!task.completedDate
          };
        });
    },
    getClaims: flow(function* getClaims() {
      const { client } = getEnv(self);
      self.claimsLoading = true;
      try {
        const { data } = yield client.mutate({
          mutation: GET_ACCOUNT_CLAIMS,
          variables: {
            accountId: self.id
          }
        });
        self.claims = data.claims;
      } finally {
        self.claimsLoading = false;
      }
    }),
    getSnapsheetExposureDetail: flow(function* getSnapsheetExposureDetail() {
      const { client } = getEnv(self);
      self.exposureDetailsLoading = true;
      try {
        const { data } = yield client.query({
          query: GET_SNAPSHEET_EXPOSURE_DETAIL,
          variables: {
            accountId: self.id
          }
        });
        self.exposureDetails = data.exposureDetails;
      } finally {
        self.exposureDetailsLoading = false;
      }
    }),
    getSnapsheetNotesDetail: flow(function* getSnapsheetNotesDetail(claimId) {
      const { client } = getEnv(self);
      self.notesDetailLoading = true;
      try {
        const { data } = yield client.query({
          query: GET_SNAPSHEET_NOTES_DETAIL,
          variables: {
            claimId
          }
        });
        self.notesDetail = data.notesDetail;
      } finally {
        self.notesDetailLoading = false;
      }
    }),
    getMemberMarketTier(policies) {
      const activePolicies = policies.filter(
        (policy) => ![PolicyStatus.Cancelled, PolicyStatus.Inactive].includes(getPolicyStatus(policy))
      );
      // If no active policies, the tier is 0 and we don't want to show the market tier card
      if (activePolicies.length === 0) {
        return 0;
      }

      const activeAutoPolicies = activePolicies.filter((policy) => policy.policyType === policyTypes.Auto);
      const activeHomePolicies = activePolicies.filter((policy) => policy.policyType === policyTypes.Home);
      const policyType = activeHomePolicies.length > 0 ? 'HA' : 'A';

      // If no active auto policies, the tier is 1
      if (activeAutoPolicies.length === 0) {
        return 1;
      }

      // If more than one active auto policy, use the oldest one
      let activeAutoPolicy = {};
      if (activeAutoPolicies.length > 1) {
        activeAutoPolicy = activeAutoPolicies.reduce((a, b) => (a.effectiveDate < b.effectiveDate ? a : b), {});
      } else {
        activeAutoPolicy = activeAutoPolicies[0];
      }

      const autoPolicyDetails = policyDetailsFromSegments(activeAutoPolicy);
      // Calculate the market tier based on the active auto policy
      return calculateMarketTier({
        policyType,
        state: activeAutoPolicy.state,
        drivers: autoPolicyDetails.drivers,
        cars: autoPolicyDetails.cars,
        priorInsuranceTerm: autoPolicyDetails.global.continuousAutoCoverageStartDate,
        biLimit: autoPolicyDetails.autoCoverage.policyLimitBIPD,
        rateControlDate: autoPolicyDetails.global.rateControlDate
      });
    }
  }))
  .views((self) => ({
    getAllTickets() {
      return self.tickets;
    },
    getFrontTickets() {
      return self.tickets.frontTickets;
    },
    getFrontPageToken({ prevPage, newPage }) {
      if (newPage > prevPage) {
        return self.tickets?.pointers?.next;
      }

      return self.tickets?.pointers?.prev.pop();
    },
    getSendgridTickets() {
      return self.tickets.sendGridEmails;
    },
    getFrontContactURL() {
      return self.frontContactURL;
    },
    get completedTasks() {
      return self.tasks.filter(({ completed }) => completed);
    },
    get incompleteTasks() {
      return self.tasks.filter(({ completed }) => !completed);
    }
  }))
  .views((self) => ({
    sortedCommunity(sortDirection) {
      if (self.community) {
        return orderBy(self.community, ['name'], [sortDirection]);
      }
      return [];
    }
  }));
